import React, { useCallback } from 'react';
import { Shield, Clock, ThumbsUp } from 'lucide-react';
import type { FormData } from '../../types/form';
import Button from '../ui/Button';
import Title from '../ui/Title';
import Description from '../ui/Description';
import Disclaimer from '../ui/Disclaimer';
import TrustpilotWidget from '../TrustpilotWidget';
import OfferCode from '../ui/OfferCode';
import { campaignVariantService } from '../../services/campaignVariants';

type StartFormProps = {
  onSubmit: (data: Partial<FormData>) => void;
};

export default function StartForm({ onSubmit }: StartFormProps) {
  const variant = campaignVariantService.getCurrentVariant();
  const heroImage = campaignVariantService.getHeroImage();

  const handleSubmit = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...(variant.offerCode && { offerCode: variant.offerCode }),
      ...(variant.firstName && { firstName: variant.firstName })
    });
  }, [onSubmit]);

  const TrustIndicator = React.memo(({ icon: Icon, label }: { icon: typeof Shield; label: string }) => (
    <div className="flex flex-col items-center">
      <div className="w-10 h-10 bg-[#212d52]/10 rounded-full flex items-center justify-center mb-2">
        <Icon className="w-5 h-5 text-[#212d52]" />
      </div>
      <span className="text-sm text-gray-600">{label}</span>
    </div>
  ));

  return (
    <div className="space-y-8">
      {heroImage && (
        <div className="relative h-48 sm:h-64 rounded-xl overflow-hidden mb-8">
          <img 
            src={heroImage}
            alt="Campaign banner"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" />
          <div className="absolute bottom-4 left-4 right-4 text-white">
            <h2 className="text-2xl font-bold mb-2">
              {campaignVariantService.getHeadline()}
            </h2>
            <p className="text-lg opacity-90">
              {campaignVariantService.getSubheadline()}
            </p>
          </div>
        </div>
      )}

      <form id="start-form" name="start-form" onSubmit={handleSubmit} className="space-y-8">
      <div className="text-center">
        {!heroImage && (
          <>
            <Title 
              id="start-form-title"
              name="start-form-title"
              as="h1" 
              className="mb-2"
            >
              {campaignVariantService.getHeadline()}
            </Title>
            <Description 
              id="start-form-description"
              name="start-form-description"
              size="lg" 
              className="mb-4"
            >
              {campaignVariantService.getSubheadline()}
            </Description>
          </>
        )}
        
        {variant.offerCode && (
          <OfferCode
            offerCode={variant.offerCode}
            size="md"
            variant="default"
            id="start-form-offer-code"
            name="start-form-offer-code"
          />
        )}
        
        <div className="mb-6">
          <TrustpilotWidget size="medium" />
        </div>
        
        <div className="grid grid-cols-3 gap-4 max-w-lg mx-auto">
          <TrustIndicator icon={Clock} label="2 Minutes" />
          <TrustIndicator icon={Shield} label="Safe & Secure" />
          <TrustIndicator icon={ThumbsUp} label="Multiple Offers" />
        </div>
      </div>

      <Button
        id="start-form-submit"
        name="start-form-submit"
        type="submit"
        fullWidth
        size="lg"
      >
        {campaignVariantService.getCtaText()}
      </Button>

      <Disclaimer
        id="start-form-disclaimer"
        name="start-form-disclaimer"
      >
        All website interactions and phone calls are recorded for marketing, compliance and quality assurance purposes. By clicking the button above or by using this website, you accept and agree to our{' '}
        <a href="https://symplelending.com/terms-of-use">Terms and Conditions</a>, <a href="https://symplelending.com/esign-consent">e-Sign</a>, <a href="https://symplelending.com/arbitration-agreement">Arbitration Agreement</a> and {' '}
        <a href="https://symplelending.com/privacy-policy">Privacy Policy</a>, including the provisions regarding accepting or avoiding cookies.
      </Disclaimer>
    </form>
    </div>
  );
}